import React from 'react';
import classNames from 'classnames';

import useFieldSize from 'hooks/useFieldSize';
import { track } from 'lib/analytics';

import Layout from './Layout';
import Text from './Text';
import Tooltip from './Tooltip';
import { DS_CLASS_NAME } from './constants';
import ToggleControl from './controls/ToggleControl';
import { FieldSize } from './types';

export interface Props {
  label: string;
  checked: boolean;
  disabled?: boolean;
  showSpinner?: boolean;
  size?: FieldSize;
  textSize?: 'body1' | 'body2' | 'body3';
  onChange: (value: boolean) => void;
  border?: boolean;
  context?: string;
  tooltipText?: string;
  hideTooltip?: boolean;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  tooltipWrap?: boolean;
}

const Toggle: React.FC<Props> = ({
  label,
  checked,
  disabled,
  context,
  showSpinner,
  onChange,
  border = true,
  textSize,
  tooltipText,
  hideTooltip = false,
  tooltipPlacement = 'top',
  tooltipWrap = false,
  ...props
}) => {
  const scaledSize = useFieldSize();
  const size = props.size || scaledSize;

  return (
    <Layout
      __className={classNames(DS_CLASS_NAME, 'Toggle', `is-size-${size}`, { 'has-border': border })}
      direction="row"
      justify="space-between"
      align="center"
    >
      <label className="Toggle-label">{textSize ? <Text size={textSize}>{label}</Text> : label}</label>
      <Tooltip
        text={tooltipText || ''}
        hidden={!tooltipText || hideTooltip}
        placement={tooltipPlacement}
        wrap={tooltipWrap}
      >
        <ToggleControl
          checked={checked}
          disabled={disabled}
          showSpinner={showSpinner}
          onChange={checked => {
            onChange(checked);
            track('Element Interacted', {
              context,
              name: label,
              type: 'toggle',
              value: checked ? 'on' : 'off'
            });
          }}
          size={size}
        />
      </Tooltip>
    </Layout>
  );
};

export default Toggle;
